import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldAlert, ShieldCheck, AlertTriangle, Phone, Users, UserCheck,
  Stethoscope, RefreshCw, ChevronRight, CheckCircle2, AlertOctagon, HelpCircle
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

export default function DataQuality() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("duplicates");

  const loadDataQuality = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/analytics/data-quality");
      setData(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDataQuality();
  }, [loadDataQuality]);

  const issues = data?.issues || {};
  const duplicates = issues.duplicate_patients || [];
  const invalidPhones = issues.invalid_phone_numbers || [];
  const missingService = issues.missing_service_type || [];
  const missingFee = issues.missing_session_fee || [];
  const unassignedHome = issues.unassigned_home_patients || [];
  const healthScore = data?.health_score || 100;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Data Quality & Integrity Review"
        subtitle="Automatic scanner for duplicate records, invalid contact details, missing fees, and unassigned home patients"
        testid="data-quality-page"
        action={
          <Button
            size="sm"
            variant="outline"
            onClick={loadDataQuality}
            className="gap-1.5 bg-white"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Scan Database
          </Button>
        }
      />

      {loading ? (
        <Loading label="Scanning database integrity..." />
      ) : (
        <>
          {/* Health Score Banner */}
          <Card className="p-5 bg-gradient-to-r from-slate-900 to-slate-800 text-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`h-16 w-16 rounded-2xl flex items-center justify-center font-bold text-2xl shadow-inner ${
                healthScore >= 90 ? "bg-emerald-500 text-white" : healthScore >= 70 ? "bg-amber-500 text-white" : "bg-red-500 text-white"
              }`}>
                {healthScore}%
              </div>
              <div>
                <h2 className="text-lg font-bold">Database Health Score</h2>
                <p className="text-xs text-slate-300 mt-0.5">
                  {healthScore >= 90
                    ? "Excellent! Patient and financial data integrity is in great condition."
                    : "Some patient profiles require attention to prevent scheduling and attendance errors."}
                </p>
                <p className="text-[11px] text-slate-400 mt-1">
                  Scanned {data?.total_active_patients || 0} active patient profiles.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="text-white border-white/20 bg-white/10 px-3 py-1">
                {duplicates.length} Duplicates
              </Badge>
              <Badge variant="outline" className="text-white border-white/20 bg-white/10 px-3 py-1">
                {invalidPhones.length} Invalid Phones
              </Badge>
              <Badge variant="outline" className="text-white border-white/20 bg-white/10 px-3 py-1">
                {unassignedHome.length} Unassigned Home
              </Badge>
            </div>
          </Card>

          {/* Issue Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid grid-cols-5 w-full bg-slate-100 p-1">
              <TabsTrigger value="duplicates" className="text-xs font-semibold">
                Duplicates ({duplicates.length})
              </TabsTrigger>
              <TabsTrigger value="invalidPhones" className="text-xs font-semibold">
                Invalid Phones ({invalidPhones.length})
              </TabsTrigger>
              <TabsTrigger value="unassignedHome" className="text-xs font-semibold">
                Unassigned Home ({unassignedHome.length})
              </TabsTrigger>
              <TabsTrigger value="missingFee" className="text-xs font-semibold">
                Missing Fee ({missingFee.length})
              </TabsTrigger>
              <TabsTrigger value="missingService" className="text-xs font-semibold">
                Missing Service ({missingService.length})
              </TabsTrigger>
            </TabsList>

            {/* 1. Duplicate Patients Tab */}
            <TabsContent value="duplicates" className="mt-4">
              <Card className="overflow-hidden border-slate-200 bg-white">
                {duplicates.length === 0 ? (
                  <div className="p-8">
                    <EmptyState
                      icon={ShieldCheck}
                      title="No duplicate patients found"
                      subtitle="All active patients have distinct phone numbers and unique identity records."
                    />
                  </div>
                ) : (
                  <Table>
                    <TableHeader className="bg-slate-50">
                      <TableRow>
                        <TableHead className="text-xs font-bold">Issue Type</TableHead>
                        <TableHead className="text-xs font-bold">Duplicate Value</TableHead>
                        <TableHead className="text-xs font-bold">First Patient</TableHead>
                        <TableHead className="text-xs font-bold">Duplicate Patient</TableHead>
                        <TableHead className="text-xs font-bold text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {duplicates.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
                              {item.type}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs font-semibold">{item.value}</TableCell>
                          <TableCell className="text-xs">
                            <span className="font-bold">{item.patient_1.name}</span> ({item.patient_1.code})
                          </TableCell>
                          <TableCell className="text-xs">
                            <span className="font-bold">{item.patient_2.name}</span> ({item.patient_2.code})
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/patients/${item.patient_2.id}`)}
                              className="h-7 text-xs"
                            >
                              Review & Merge
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </TabsContent>

            {/* 2. Invalid Phone Numbers Tab */}
            <TabsContent value="invalidPhones" className="mt-4">
              <Card className="overflow-hidden border-slate-200 bg-white">
                {invalidPhones.length === 0 ? (
                  <div className="p-8">
                    <EmptyState
                      icon={CheckCircle2}
                      title="All phone numbers are valid"
                      subtitle="All active patient contact numbers match the 10-digit Indian standard."
                    />
                  </div>
                ) : (
                  <Table>
                    <TableHeader className="bg-slate-50">
                      <TableRow>
                        <TableHead className="text-xs font-bold">Patient Code</TableHead>
                        <TableHead className="text-xs font-bold">Patient Name</TableHead>
                        <TableHead className="text-xs font-bold">Current Phone Entry</TableHead>
                        <TableHead className="text-xs font-bold">Issue</TableHead>
                        <TableHead className="text-xs font-bold text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invalidPhones.map((p, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono text-xs text-slate-500">{p.patient_code}</TableCell>
                          <TableCell className="text-xs font-bold">{p.name}</TableCell>
                          <TableCell className="text-xs font-mono text-red-600 font-semibold">{p.phone || "(empty)"}</TableCell>
                          <TableCell className="text-xs text-slate-500">Not 10-digit Indian mobile</TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/patients/${p.id}`)}
                              className="h-7 text-xs"
                            >
                              Edit Phone
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </TabsContent>

            {/* 3. Unassigned Home Patients Tab */}
            <TabsContent value="unassignedHome" className="mt-4">
              <Card className="overflow-hidden border-slate-200 bg-white">
                {unassignedHome.length === 0 ? (
                  <div className="p-8">
                    <EmptyState
                      icon={CheckCircle2}
                      title="All home patients assigned"
                      subtitle="Every active home physiotherapy patient is designated to a primary physiotherapist."
                    />
                  </div>
                ) : (
                  <Table>
                    <TableHeader className="bg-slate-50">
                      <TableRow>
                        <TableHead className="text-xs font-bold">Patient Code</TableHead>
                        <TableHead className="text-xs font-bold">Patient Name</TableHead>
                        <TableHead className="text-xs font-bold">Locality / Address</TableHead>
                        <TableHead className="text-xs font-bold text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {unassignedHome.map((p, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono text-xs text-slate-500">{p.code}</TableCell>
                          <TableCell className="text-xs font-bold">{p.name}</TableCell>
                          <TableCell className="text-xs text-slate-600">{p.locality || "-"}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              onClick={() => navigate(`/home-dashboard`)}
                              className="h-7 text-xs bg-bk-blue hover:bg-blue-700 text-white"
                            >
                              Assign Physio
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </TabsContent>

            {/* 4. Missing Session Fee Tab */}
            <TabsContent value="missingFee" className="mt-4">
              <Card className="overflow-hidden border-slate-200 bg-white">
                {missingFee.length === 0 ? (
                  <div className="p-8">
                    <EmptyState
                      icon={CheckCircle2}
                      title="All session fees configured"
                      subtitle="Every active patient has a valid treatment session fee configured."
                    />
                  </div>
                ) : (
                  <Table>
                    <TableHeader className="bg-slate-50">
                      <TableRow>
                        <TableHead className="text-xs font-bold">Patient Code</TableHead>
                        <TableHead className="text-xs font-bold">Patient Name</TableHead>
                        <TableHead className="text-xs font-bold text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {missingFee.map((p, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono text-xs text-slate-500">{p.code}</TableCell>
                          <TableCell className="text-xs font-bold">{p.name}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/patients/${p.id}`)}
                              className="h-7 text-xs"
                            >
                              Set Fee
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </TabsContent>

            {/* 5. Missing Service Type Tab */}
            <TabsContent value="missingService" className="mt-4">
              <Card className="overflow-hidden border-slate-200 bg-white">
                {missingService.length === 0 ? (
                  <div className="p-8">
                    <EmptyState
                      icon={CheckCircle2}
                      title="All service types defined"
                      subtitle="Every patient is classified as Clinic, Home, or Both."
                    />
                  </div>
                ) : (
                  <Table>
                    <TableHeader className="bg-slate-50">
                      <TableRow>
                        <TableHead className="text-xs font-bold">Patient Code</TableHead>
                        <TableHead className="text-xs font-bold">Patient Name</TableHead>
                        <TableHead className="text-xs font-bold text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {missingService.map((p, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono text-xs text-slate-500">{p.code}</TableCell>
                          <TableCell className="text-xs font-bold">{p.name}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/patients/${p.id}`)}
                              className="h-7 text-xs"
                            >
                              Configure
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
